import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import React, { useContext, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import ValidationMessage from 'src/components/validationMessage'
import Button from '@dustin-web/microsite-components/src/button'
import Alert from 'src/components/alert'
import Spinner from '@dustin-web/microsite-components/src/spinner'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  MergeCartsById,
  MergeCartsByIdMutation,
  MergeCartsByIdMutationVariables,
  RegisterBusinessFederated,
  RegisterBusinessFederatedMutation,
  RegisterBusinessFederatedMutationVariables,
  RegisterCustomerStatus,
} from 'src/graphql/generated/graphql-types'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import CustomRedirect from 'src/components/customRedirect'
import AppContext from 'src/appContext'
import { accountCreated } from 'src/util/googleAnalyticsWrapperV2'
import { getAccountTermsUrl } from 'src/helperFunctions'

export type FormDataType = {
  acceptAccountTerms: boolean
  offersByEmail: boolean
  email: string
}

export type Props = {
  companyName?: string | null
  fullName: string
  email: string
  returnUrl: string | null
  cartId: number | null
}

enum State {
  edit,
  loading,
  error,
  redirect,
}

const resolver: Resolver<FormDataType> = async values => {
  return {
    values: values,
    errors: !values.acceptAccountTerms
      ? {
        acceptAccountTerms: {
          type: 'required',
        },
      }
      : {},
  }
}

const Form = ({ companyName, fullName, email, returnUrl, cartId }: Props) => {
  const { translate } = useTranslations()
  const [state, setState] = useState<State>(State.edit)
  const [redirectLink, setRedirectLink] = useState<string | null>(null)
  const { setCurrentContact } = useAuth()
  const { sourcingCompany } = useContext(AppContext)

  const { site } = useSiteContext()
  const accountTermsUrl = getAccountTermsUrl(site)

  const defaultValues: FormDataType = {
    acceptAccountTerms: false,
    offersByEmail: false,
    email,
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver,
  })

  const [registerBusiness] = useGraphqlMutation<
    RegisterBusinessFederatedMutation,
    RegisterBusinessFederatedMutationVariables
  >(RegisterBusinessFederated)

  const [mergeCartsById] = useGraphqlMutation<
    MergeCartsByIdMutation,
    MergeCartsByIdMutationVariables
  >(MergeCartsById)

  const submit = async (form: FormDataType) => {
    setState(State.loading)

    const data = await registerBusiness({
      variables: {
        accountTermsAccepted: form.acceptAccountTerms,
        offersByEmail: form.offersByEmail,
        email: form.email ? form.email : email,
      },
    }).catch(() => {
      setState(State.error)
    })

    if (
      data?.registerBusiness?.status === RegisterCustomerStatus.Success &&
      data?.registerBusiness?.contactId
    ) {
      accountCreated(sourcingCompany)

      try {
        if (!!cartId) {
          await mergeCartsById({
            variables: {
              sourceCartId: cartId,
              targetContactId: data.registerBusiness.contactId,
            },
          })
        }
      } catch (error) { }

      await setCurrentContact(data.registerBusiness.contactId)

      setRedirectLink(returnUrl ?? '/')

      setState(State.redirect)
    } else {
      setState(State.error)
    }
  }

  if (state === State.redirect && redirectLink) {
    return <CustomRedirect url={redirectLink} />
  }

  return (
    <>
      {state === State.loading && <Spinner dustinSpinner />}

      <div className="row u-pb-12">
        <div className="col">
          <h2>Hi {fullName}!</h2>
          <p>Do you wish to join {companyName}?</p>
        </div>
      </div>
      <div className="row">
        <div className="col col-md-10 col-lg-8">
          <form className="c-form c-form--block u-pb-16 u-pb-xl-0" onSubmit={handleSubmit(submit)}>
            <div className="row">
              <div className="col">
                <div className="c-form__item">
                  <div className="c-checkbox">
                    <input
                      type="checkbox"
                      id="register-offers-by-email"
                      {...register('offersByEmail')}
                    />
                    <label htmlFor="register-offers-by-email">
                      {translate('Register_OffersByEmail')}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col">
                <div className="c-form__item">
                  <div className="c-checkbox">
                    <input
                      type="checkbox"
                      id="register-accept-account-terms"
                      {...register('acceptAccountTerms')}
                      aria-invalid={errors.acceptAccountTerms ? 'true' : 'false'}
                      aria-describedby={
                        errors.acceptAccountTerms ? 'acceptAccountTermsError' : undefined
                      }
                    />
                    <label htmlFor="register-accept-account-terms">
                      {translate('Register_AcceptAccountTerms')}&nbsp;
                      <a href={accountTermsUrl} target="_blank" rel="noreferrer">
                        {translate('Register_AcceptAccountTerms_LinkText')}
                      </a>
                    </label>
                  </div>
                  {errors.acceptAccountTerms && (
                    <ValidationMessage id="acceptAccountTermsError">
                      {translate('Validation_AcceptAccountTermsIsRequired')}
                    </ValidationMessage>
                  )}
                </div>
              </div>
            </div>

            <Button
              text={translate('Register_SubmitButton')}
              className="u-sm-w-100"
              primary
              type="submit"
            />

            {state === State.error && <Alert variant="danger">{translate('Register_Error')}</Alert>}
          </form>
        </div>
      </div>
    </>
  )
}

export default Form
